import { CardLoader } from 'components/organisms'
import withLoader from 'hocs/with-loader'
import PropTypes from 'prop-types'
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  UncontrolledPopover,
  PopoverHeader,
  PopoverBody
} from 'reactstrap'
import { useEffect, useMemo, useState } from 'react'
import { FieldArray, Formik } from 'formik'
import { InputField, InputSelectOptions } from 'components/molecules'
import {
  PAYMENT_METHOD,
  DEAL_STATE,
  SALES_METHOD,
  DEAL_PAYMENT_METHOD_OPTIONS,
  EDC_PAYMENT_METHODS,
  CARD_TYPE_OPTIONS,
  SALES_METHOD_NOTE_REQUIRED_METHODS,
  SALES_METHOD_NOTE_LABEL_BY_METHOD,
} from '../constants'
import QRCode from 'react-qr-code'
import StringUtil from 'utils/string-util'
import { formatThousandsInput, sanitizeThousandsInput } from 'utils/thousands-input-util'
import TimeUtil from 'utils/time-util'
import { PAYMENT_STATE } from 'domains/finance/payments/constants'
import TableUtil from 'utils/table-util'
import { useAuth } from 'providers/auth-provider'
import { printCustomerReceipt, printInvoice, emailInvoice } from 'utils/invoice-util'
import SalesAliasSelectSearchField from '../organisms/sales-alias-select-search-field'
import SalesNameSelectSearchField from '../organisms/sales-name-select-search-field'
import SalesDealsApi from 'api/admins/sales-deals-api'

// extracted component
import UploadTransferReceiptCard from './UploadTransferReceiptCard'

const propTypes = {
  deal: PropTypes.object,
  isOwner: PropTypes.bool,
  fetchDeal: PropTypes.func,
  updateDeal: PropTypes.func,
  updateFinalPrices: PropTypes.func,
  cancelDeal: PropTypes.func,
  cancelDealItem: PropTypes.func,
  fetchHistoryList: PropTypes.func,
  historyList: PropTypes.array,
  fetchHistoryDetail: PropTypes.func,
  historyDetail: PropTypes.object,
  clearHistoryDetail: PropTypes.func,
}

const defaultValues = {
  deal: {},
  isOwner: false,
  fetchDeal: () => {},
  updateDeal: () => Promise.resolve(),
  updateFinalPrices: () => Promise.resolve(),
  cancelDeal: () => Promise.resolve(),
  cancelDealItem: () => Promise.resolve(),
  fetchHistoryList: () => {},
  historyList: [],
  fetchHistoryDetail: () => {},
  historyDetail: {},
  clearHistoryDetail: () => {},
}

const extractApiErrorMessage = (error, fallback = 'Oops, Something wrong') => {
  const data = error?.response?.data

  if (typeof data === 'string' && data.trim()) return data
  if (typeof data?.message === 'string' && data.message.trim()) return data.message
  if (Array.isArray(data?.message) && data.message.length > 0) return data.message.join(', ')
  if (Array.isArray(data?.errors) && data.errors.length > 0) return data.errors.join(', ')

  if (data?.errors && typeof data.errors === 'object') {
    const flattened = Object.values(data.errors).flat().filter(Boolean)
    if (flattened.length > 0) return flattened.join(', ')
  }

  if (typeof error?.message === 'string' && error.message.trim()) return error.message

  return fallback
}

const getPaymentMethodOptionValue = (methodType = '', cardType = '') => {
  const normalizedMethod = (methodType || '').toString().trim().toLowerCase()
  const normalizedCardType = (cardType || '').toString().trim().toLowerCase()

  if (normalizedMethod === PAYMENT_METHOD.CREDIT_CARD) {
    if (normalizedCardType === 'amex') return 'credit_card__amex'
    if (normalizedCardType === 'non_amex') return 'credit_card__non_amex'
    return PAYMENT_METHOD.CREDIT_CARD
  }

  return normalizedMethod
}

const paymentMethodLabel = (methodType = '', cardType = '') => {
  const normalizedCardType = (cardType || '').toString().trim().toLowerCase()

  switch (methodType) {
  case PAYMENT_METHOD.VIRTUAL_ACCOUNT:
    return 'MIDTRANS (Virtual Account)'
  case PAYMENT_METHOD.MIDTRANS:
    return 'MIDTRANS (Virtual Account)'
  case PAYMENT_METHOD.CREDIT_CARD:
    if (normalizedCardType === 'amex') return 'Midtrans (CC) - Amex'
    if (normalizedCardType === 'non_amex') return 'Midtrans (CC) - Non Amex'
    return 'Midtrans (CC)'
  default:
    return StringUtil.capitalizeAll(StringUtil.humanize(methodType || ''))
  }
}

const paymentCardTypeLabel = (methodType = '', cardType = '') => {
  const normalizedMethod = (methodType || '').toString().trim().toLowerCase()
  const normalizedCardType = (cardType || '').toString().trim().toLowerCase()

  if (normalizedMethod === PAYMENT_METHOD.CREDIT_CARD) {
    if (normalizedCardType === 'amex') return 'Amex'
    if (normalizedCardType === 'non_amex') return 'Non Amex'
    return '-'
  }

  return cardType ? StringUtil.capitalizeAll(StringUtil.humanize(cardType)) : '-'
}

const isPoPlaceholderProduct = (product = {}) => {
  const sku = (product?.sku || '').toString().trim().toUpperCase()
  const name = (product?.name || '').toString().trim()

  return sku.startsWith('PO-') && /^PO\s*Placeholder/i.test(name)
}

const isPoSkuProduct = (product = {}) => {
  const sku = (product?.sku || '').toString().trim().toUpperCase()
  return sku.startsWith('PO-')
}

const getPoProductDesignerName = (product = {}) => {
  return product?.designer?.name || product?.summary_index?.designer || '-'
}

const getPoProductCategoryName = (product = {}) => {
  return product?.category?.name || product?.summary_index?.category || '-'
}

const getPoProductConditionName = (product = {}) => {
  return product?.condition?.name || product?.summary_index?.condition || '-'
}

const getPoProductComesWith = (product = {}) => {
  return product?.comes_with || '-'
}

const getFirstPoCartItemFromDeal = (deal = {}) => {
  return (deal?.cart?.cart_items || []).find((cartItem) => {
    return isPoSkuProduct(cartItem?.product)
  }) || null
}

const PO_WATCH_DESIGNER_CODES = ['PI', 'AP', 'CT', 'BT', 'BU', 'CP', 'FM', 'HB', 'OM', 'PA', 'PP', 'RM', 'RX', 'ZE', 'TD', 'TH']

const normalizePoCode = (value = '') => {
  return (value || '').toString().trim().toUpperCase()
}

const inferPoInternalCategory = (designer = {}, category = {}) => {
  const designerCode = normalizePoCode(designer?.code)
  const categoryCode = normalizePoCode(category?.code)

  if (categoryCode === '04' && PO_WATCH_DESIGNER_CODES.includes(designerCode)) return 'W'
  if (designerCode === 'HR' && categoryCode === '01') return 'H'

  return 'C'
}

const getPoPlaceholderSerial = (product = {}) => {
  const sources = [product?.name, product?.sku]

  for (const source of sources) {
    const match = (source || '').toString().trim().match(/(\d+)$/)
    if (match) {
      const parsed = parseInt(match[1], 10)
      return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : match[1]
    }
  }

  return ''
}

const getPoPlaceholderNameLabel = (internalCategory = '') => {
  switch (normalizePoCode(internalCategory)) {
  case 'H':
    return 'Hermes Bag'
  case 'W':
    return 'Watch'
  default:
    return 'Other'
  }
}

const buildPoPlaceholderNamePreview = (product = {}, designer = {}, category = {}) => {
  if (!product) return '-'
  if (!designer?.id || !category?.id) return product?.name || product?.sku || '-'

  const internalCategory = inferPoInternalCategory(designer, category)
  const label = getPoPlaceholderNameLabel(internalCategory)
  const serial = getPoPlaceholderSerial(product)

  return ['PO Placeholder', label, serial].filter(Boolean).join(' ')
}

const padPoPlaceholderSkuSerial = (serial = '') => {
  const value = (serial || '').toString().trim()
  return value ? value.padStart(3, '0') : ''
}

const buildPoPlaceholderSkuPreview = (product = {}, designer = {}, category = {}) => {
  if (!product) return '-'
  if (!designer?.id || !category?.id) return product?.sku || '-'

  const internalCategory = inferPoInternalCategory(designer, category)
  const serial = padPoPlaceholderSkuSerial(getPoPlaceholderSerial(product))

  return ['PO', internalCategory, serial].filter(Boolean).join('-') || product?.sku || '-'
}

const salesDealProductDetailTitle = (product = {}) => {
  if (!product) return '-'
  if (isPoSkuProduct(product)) return product?.name || product?.sku || '-'
  return product?.name || product?.sku || '-'
}

const salesDealProductInventoryHref = (product = {}) => {
  const groupCode = (product?.group?.code || '').toString().trim().toUpperCase()
  const inventoryId = product?.inventory?.id

  if (!inventoryId || ['PO', 'BKO'].includes(groupCode)) return null
  return `/app/warehouse-team/inventory/${inventoryId}`
}

const isDealItemCancellationUiSupported = (cartItem = {}) => {
  const product = cartItem?.product || {}
  const groupCode = (product?.group?.code || '').toString().trim().toUpperCase()
  const sku = (product?.sku || '').toString().trim().toUpperCase()
  const quantity = Number(cartItem?.quantity || 1)

  if (quantity !== 1) return false
  if (['PO', 'BKO'].includes(groupCode)) return false
  if (sku.startsWith('PO-') || sku.startsWith('BKO-')) return false

  return true
}

const salesDealProductDisplayName = (product = {}) => {
  if (!product) return '-'
  if (isPoPlaceholderProduct(product)) return product?.sku || '-'
  return product?.name || product?.sku || '-'
}

const salesDealProductConsignorName = (product = {}) => {
  return product?.consignor_name ||
    product?.consignment?.consignor_detail?.name ||
    product?.consignment?.user?.detail?.name ||
    product?.supplier_name ||
    '-'
}

const hasSalesDealProductPrice = (value) => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string' && value.trim() === '') return false

  return true
}

const salesDealProductCurrencyLabel = (value) => {
  if (!hasSalesDealProductPrice(value)) return '-'

  return StringUtil.currencyCase(value || 0)
}

const salesDealProductBuyingPriceVisible = (product = {}) => {
  return product?.hm_buying_price_visible === true && hasSalesDealProductPrice(product?.buying_price)
}

const GATEWAY_FINAL_PRICE_LOCK_METHODS = [
  'midtrans',
  'credit_card',
  'virtual_account',
]

const IGNORED_PAYMENT_STATES_FOR_FINAL_PRICE_LOCK = [
  'failed',
  'fail',
  'cancelled',
  'canceled',
  'expired',
  'expire',
  'deny',
  'denied',
]

const isGatewayFinalPriceLockMethod = (method = '') => {
  return GATEWAY_FINAL_PRICE_LOCK_METHODS.includes(
    (method || '').toString().trim().toLowerCase()
  )
}

const isIgnoredPaymentStateForFinalPriceLock = (state = '') => {
  return IGNORED_PAYMENT_STATES_FOR_FINAL_PRICE_LOCK.includes(
    (state || '').toString().trim().toLowerCase()
  )
}

const buildPaymentMethodOptions = (deal = {}) => {
  const currentMethod = deal?.cart?.payment_method || deal?.payment?.method_type || ''
  const currentCardType = deal?.payment?.card_type || ''
  const currentOptionValue = getPaymentMethodOptionValue(currentMethod, currentCardType)
  const base = [...DEAL_PAYMENT_METHOD_OPTIONS]

  if (!currentMethod) return base

  const exists = base.some(o => (o.option_value || o.value) === currentOptionValue)
  if (exists) return base

  return [
    {
      label: paymentMethodLabel(currentMethod, currentCardType),
      value: currentMethod,
      installment: deal?.cart?.installment || null,
      option_value: currentOptionValue,
      card_type: currentCardType || null,
    },
    ...base,
  ]
}

const buildCurrentCourierOption = (deal = {}) => {
  const currentRateId =
    deal?.cart?.shipping_rate_id ||
    deal?.shipment?.rate_id ||
    deal?.shipment?.courier_detail?.rate?.id

  if (!currentRateId) return null

  const detail = deal?.shipment?.courier_detail || {}

  return {
    logistic: detail.logistic || {
      id: currentRateId,
      name: 'Current Courier',
      company_name: 'Current Courier',
    },
    rate: detail.rate || {
      id: currentRateId,
      name: 'Current Rate',
      type: '',
      description: '',
      full_description: '',
    },
    duration_min: detail.duration_min,
    duration_max: detail.duration_max,
    duration_unit: detail.duration_unit,
    total_price: detail.total_price ?? deal?.shipment?.delivery_fee ?? 0,
    insurance_fee: detail.insurance_fee ?? deal?.shipment?.insurance_fee ?? 0,
  }
}

const courierOptionLabel = (option = {}) => {
  const logisticName = option?.logistic?.name || '-'
  const rateName = option?.rate?.name || '-'
  const totalPrice = Number(option?.total_price || 0)

  return `${logisticName} - ${rateName} (${StringUtil.currencyCase(totalPrice)})`
}

const normalizePayments = (deal) => {
  const list = Array.isArray(deal?.payments) ? deal.payments.filter(Boolean) : []
  if (list.length > 0) return list
  if (deal?.payment) return [deal.payment]
  return []
}

const sortPaymentsAsc = (payments) => {
  return (payments || []).slice().sort((a, b) => {
    const ta = new Date(a?.created_at || a?.updated_at || 0).getTime()
    const tb = new Date(b?.created_at || b?.updated_at || 0).getTime()
    return ta - tb
  })
}

const pickDefaultPaymentId = (paymentsAsc) => {
  if (!paymentsAsc.length) return null
  // prefer latest pending, else last
  const pending = paymentsAsc.filter(p => p?.state === PAYMENT_STATE.PENDING)
  const target = pending.length ? pending[pending.length - 1] : paymentsAsc[paymentsAsc.length - 1]
  return target?.id || null
}

const buildEditForm = (deal = {}) => {
  const currentPaymentMethod = deal?.cart?.payment_method || deal?.payment?.method_type || ''
  const shouldKeepCardType =
    EDC_PAYMENT_METHODS.includes(currentPaymentMethod) ||
    currentPaymentMethod === PAYMENT_METHOD.CREDIT_CARD

  const assistByList =
    Array.isArray(deal?.assist_by_list) && deal.assist_by_list.length > 0
      ? deal.assist_by_list.map(x => (x || '').toString().trim()).filter(Boolean)
      : (deal?.assist_by || '')
        .split(',')
        .map(x => x.trim())
        .filter(Boolean)

  const firstPoProduct = getFirstPoCartItemFromDeal(deal)?.product || {}

  return {
    sales_method: deal?.sales_method || '',
    sales_method_note: deal?.sales_method_note || '',
    sales_name_in_invoice: deal?.sales_name_in_invoice || '',
    deal_by: deal?.deal_by || '',
    assist_by_list: assistByList.length > 0 ? assistByList : [''],
    payment_method: getPaymentMethodOptionValue(
      deal?.cart?.payment_method || deal?.payment?.method_type || '',
      deal?.payment?.card_type || ''
    ),
    rate_id: deal?.cart?.shipping_rate_id ? String(deal.cart.shipping_rate_id) : '',
    card_type: shouldKeepCardType ? deal?.payment?.card_type || '' : '',
    pickup_at: deal?.pickup_at ? TimeUtil.format(deal.pickup_at, 'YYYY-MM-DD') : '',
    note: deal?.note || '',
    include_invoice: !!deal?.include_invoice,
    po_designer_id: firstPoProduct?.designer_id ? String(firstPoProduct.designer_id) : '',
    po_category_id: firstPoProduct?.category_id ? String(firstPoProduct.category_id) : '',
    po_condition_id: firstPoProduct?.condition_id ? String(firstPoProduct.condition_id) : '',
    po_comes_with: firstPoProduct?.comes_with || '',
    include_polaroid: !!deal?.include_polaroid,
    include_love_letter: !!deal?.include_love_letter,
  }
}

const actionButtonGroupStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '8px',
}

const actionButtonStyle = {
  minWidth: '180px',
  maxWidth: '100%',
}


/**
 * Displaying detail of Deal in table view
 */
const SalesDealsView = ({
  deal,
  isOwner,
  fetchDeal,
  updateDeal,
  updateFinalPrices,
  cancelDeal,
  cancelDealItem,
  fetchHistoryList,
  historyList,
  fetchHistoryDetail,
  historyDetail,
  clearHistoryDetail,
}) => {

  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [showDifferenceOnly, setShowDifferenceOnly] = useState(false)

  const [selectedPaymentId, setSelectedPaymentId] = useState(null)

  // Email invoice status UI
  const [isEmailingInvoice, setIsEmailingInvoice] = useState(false)
  const [emailInvoiceResult, setEmailInvoiceResult] = useState(null) // { color, message }
  const [isMultipleEmailInvoiceOpen, setIsMultipleEmailInvoiceOpen] = useState(false)
  const [multipleInvoiceRecipients, setMultipleInvoiceRecipients] = useState([])
  const [newMultipleInvoiceRecipient, setNewMultipleInvoiceRecipient] = useState('')
  const [multipleEmailInvoiceError, setMultipleEmailInvoiceError] = useState(null)

  const [isPrintingCustomerReceipt, setIsPrintingCustomerReceipt] = useState(false)
  const [customerReceiptResult, setCustomerReceiptResult] = useState(null)
  const [isPrintingInvoice, setIsPrintingInvoice] = useState(false)
  const [printInvoiceResult, setPrintInvoiceResult] = useState(null)
  const [showPrintHeaderAndFooter, setShowPrintHeaderAndFooter] = useState(true)


  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [editResult, setEditResult] = useState(null)

  const [isFinalPriceOpen, setIsFinalPriceOpen] = useState(false)
  const [isSavingFinalPrice, setIsSavingFinalPrice] = useState(false)
  const [finalPriceResult, setFinalPriceResult] = useState(null)
  const [finalPriceForm, setFinalPriceForm] = useState({})

  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false)
  const [isCancellingDeal, setIsCancellingDeal] = useState(false)
  const [cancelResult, setCancelResult] = useState(null)

  const [cancelItemTarget, setCancelItemTarget] = useState(null)
  const [cancelItemReason, setCancelItemReason] = useState('')
  const [isCancellingItem, setIsCancellingItem] = useState(false)
  const [cancelItemResult, setCancelItemResult] = useState(null)

  const [courierOptions, setCourierOptions] = useState([])
  const [isLoadingCourierOptions, setIsLoadingCourierOptions] = useState(false)

  const [poMeta, setPoMeta] = useState({ designers: [], categories: [], conditions: [] })
  const [isPoMetaLoading, setIsPoMetaLoading] = useState(false)

  const [brokenDownPaymentProofMap, setBrokenDownPaymentProofMap] = useState({})

  const permissions = deal?.permissions || {}
  const isDealInAssistOnlyState = !!permissions?.assist_only_edit
  const canEditDeal = permissions?.can_full_edit !== undefined
    ? !!permissions.can_full_edit
    : !!permissions?.can_edit
  const canEditAssist = !!permissions?.can_edit_assist
  const isAssistOnlyEdit = isDealInAssistOnlyState && canEditAssist
  const canOpenEditDeal = canEditDeal || isAssistOnlyEdit
  const financeLocked = !!permissions?.finance_locked
  const editLockReason = permissions?.lock_reason
  const canCancelDeal = !!permissions?.can_cancel
  const cancelLockReason = permissions?.cancel_lock_reason
  const canCancelItem = !!permissions?.can_cancel_item

  const paymentMethodOptions = useMemo(() => buildPaymentMethodOptions(deal), [deal])
  const currentCourierOption = useMemo(() => buildCurrentCourierOption(deal), [deal])

  const downPaymentProofUrls = Array.isArray(deal?.down_payment_proof_urls)
    ? deal.down_payment_proof_urls.filter(Boolean)
    : []

  const hasExistingDownPaymentProofs = downPaymentProofUrls.length > 0

  // Legacy DP Proof hanya tampil read-only untuk data historis.
  // Upload baru dilakukan lewat payment-specific receipt uploader.
  const shouldShowDownPaymentProofRow = hasExistingDownPaymentProofs

  const isPdfUrl = (url = '') => {
    return url.toLowerCase().split('?')[0].endsWith('.pdf')
  }

  const loadCourierOptions = async () => {
    setIsLoadingCourierOptions(true)

    try {
      const latitude =
        Number(
          deal?.shipment?.address_detail?.latitude ??
          deal?.shipment?.destination?.lat ??
          deal?.shipment?.destination?.latitude
        ) || null

      const longitude =
        Number(
          deal?.shipment?.address_detail?.longitude ??
          deal?.shipment?.destination?.lng ??
          deal?.shipment?.destination?.longitude
        ) || null

      const product_id_with_selling_price_list = (deal?.cart?.cart_items || []).map((cartItem) => ({
        product_id: cartItem?.product?.id || cartItem?.product_id,
        selling_price: Number(cartItem?.price || 0),
      }))

      const response = await SalesDealsApi.couriers({
        latitude,
        longitude,
        product_id_with_selling_price_list,
      })

      let options = Array.isArray(response?.data?.couriers) ? response.data.couriers : []

      if (
        currentCourierOption &&
        !options.some(o => String(o?.rate?.id) === String(currentCourierOption?.rate?.id))
      ) {
        options = [currentCourierOption, ...options]
      }

      setCourierOptions(options)
    } catch (error) {
      setCourierOptions(currentCourierOption ? [currentCourierOption] : [])
    } finally {
      setIsLoadingCourierOptions(false)
    }
  }

  const loadPoMetaOptions = async () => {
    if (poMeta.designers.length > 0 && poMeta.categories.length > 0 && poMeta.conditions.length > 0) return

    setIsPoMetaLoading(true)

    try {
      const response = await SalesDealsApi.salesProductMeta()

      setPoMeta({
        designers: response?.data?.data?.designers || [],
        categories: response?.data?.data?.categories || [],
        conditions: response?.data?.data?.conditions || [],
      })
    } catch (error) {
      setPoMeta({ designers: [], categories: [], conditions: [] })
    } finally {
      setIsPoMetaLoading(false)
    }
  }

  useEffect(() => {
    if (isEditOpen) {
      loadCourierOptions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditOpen, deal?.id])

  useEffect(() => {
    setBrokenDownPaymentProofMap({})
  }, [deal?.id, downPaymentProofUrls.join('|')])

  const handleConfirmCancelDeal = async () => {
    setCancelResult(null)
    setIsCancellingDeal(true)

    try {
      await cancelDeal()
      setIsCancelConfirmOpen(false)
    } catch (err) {
      // biarkan parent/page handle alert error
      setCancelResult({
        color: 'danger',
        message: extractApiErrorMessage(err, 'Failed to cancel deal.')
      })
    } finally {
      setIsCancellingDeal(false)
    }
  }

  const handleOpenCancelItem = (cartItem) => {
    setCancelItemTarget(cartItem)
    setCancelItemReason('')
    setCancelItemResult(null)
  }

  const handleCloseCancelItem = () => {
    if (isCancellingItem) return

    setCancelItemTarget(null)
    setCancelItemReason('')
    setCancelItemResult(null)
  }

  const handleConfirmCancelItem = async () => {
    if (!cancelItemTarget?.id || !cancelItemReason.trim()) return

    setCancelItemResult(null)
    setIsCancellingItem(true)

    try {
      await cancelDealItem({
        cart_item_id: cancelItemTarget.id,
        reason: cancelItemReason.trim(),
        cancel_deal_if_last_item: isLastDealItem,
      })
      setCancelItemTarget(null)
      setCancelItemReason('')
    } catch (err) {
      setCancelItemResult({
        color: 'danger',
        message: extractApiErrorMessage(err, 'Failed to cancel deal item.')
      })
    } finally {
      setIsCancellingItem(false)
    }
  }

  useEffect(() => {
    setCancelResult(null)
    setCancelItemTarget(null)
    setCancelItemReason('')
    setCancelItemResult(null)
  }, [deal?.id, deal?.state])

  const buildFinalPriceForm = (deal = {}) => {
    const result = {}

    ;(deal?.cart?.cart_items || []).forEach((cartItem) => {
      const id = cartItem?.id
      if (!id) return
      result[id] = String(cartItem?.price || '')
    })

    return result
  }

  useEffect(() => {
    setFinalPriceForm(buildFinalPriceForm(deal))
    setFinalPriceResult(null)
  }, [deal])

  useEffect(() => {
    if (isHistoryOpen) {
      fetchHistoryList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHistoryOpen])

  useEffect(() => {
    setEditResult(null)
  }, [deal])

  const tableData = TableUtil.convertHistoryDetailIntoTableData(historyDetail || {})

  const hasPoSkuDeal = useMemo(() => {
    return (deal?.cart?.cart_items || []).some((cartItem) => {
      const sku = (cartItem?.product?.sku || '').toString().trim().toUpperCase()
      return sku.startsWith('PO-')
    })
  }, [deal])

  const firstPoCartItem = useMemo(() => {
    return getFirstPoCartItemFromDeal(deal)
  }, [deal])

  const firstPoProduct = firstPoCartItem?.product || null

  useEffect(() => {
    if (isEditOpen && hasPoSkuDeal) loadPoMetaOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditOpen, hasPoSkuDeal])

  const paymentsAsc = useMemo(() => {
    return sortPaymentsAsc(normalizePayments(deal))
  }, [deal?.payments, deal?.payment])



  const [isPaymentHistoryOpen, setIsPaymentHistoryOpen] = useState(false)

  const hasGatewayPaymentForFinalPrice = useMemo(() => {
    const payments = paymentsAsc.length > 0 ? paymentsAsc : [deal?.payment].filter(Boolean)

    const hasActiveGatewayPayment = payments.some((payment) => {
      if (isIgnoredPaymentStateForFinalPriceLock(payment?.state)) return false
      return isGatewayFinalPriceLockMethod(payment?.method_type)
    })

    if (hasActiveGatewayPayment) return true

    if (payments.length === 0) {
      return isGatewayFinalPriceLockMethod(deal?.cart?.payment_method)
    }

    return false
  }, [paymentsAsc, deal?.payment, deal?.cart?.payment_method])

  // Once Buying starts / finishes sourcing, Final Price must stay immutable.
  // This mirrors the backend guard and is intentionally independent of Finance CF.
  const isFinalPriceLockedBySourcingState =
    ['on_sources', 'sourced'].includes(deal?.state)

  // Keep the existing gateway-payment lock and combine it with the sourcing lock.
  const isFinalPriceLocked =
    hasGatewayPaymentForFinalPrice || isFinalPriceLockedBySourcingState

  // Show the most relevant reason for why Final Price is locked.
  const finalPriceLockMessage = isFinalPriceLockedBySourcingState
    ? 'Final Price tidak bisa diedit saat deal sudah On Sources / Sourced.'
    : 'Final price tidak bisa diubah untuk deal dengan metode pembayaran Midtrans / Credit Card / Virtual Account.'

  useEffect(() => {
    // when deal changes, re-pick default payment
    const nextDefault = pickDefaultPaymentId(paymentsAsc)
    setSelectedPaymentId(nextDefault)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id])

  useEffect(() => {
    if (!Array.isArray(deal?.payments)) return

    // re-pick selected payment after deal refresh
    if (selectedPaymentId) {
      const exists = deal.payments.some(p => p.id === selectedPaymentId)
      if (!exists) {
        setSelectedPaymentId(deal.payments[deal.payments.length - 1]?.id)
      }
    }
  }, [deal?.payments])

  const selectedPayment = useMemo(() => {
    if (!Array.isArray(deal?.payments)) return null

    const list = deal.payments
    if (!list.length) return null

    return list.find(p => p.id === selectedPaymentId) || list[list.length - 1]
  }, [deal?.payments, selectedPaymentId])

  const paymentIndex = useMemo(() => {
    if (!selectedPayment?.id) return null
    const idx = paymentsAsc.findIndex(p => p?.id === selectedPayment.id)
    return idx >= 0 ? (idx + 1) : null
  }, [paymentsAsc, selectedPayment])

  const selectedPaymentReceipts = useMemo(() => {
    if (!selectedPayment) return []

    if (Array.isArray(selectedPayment?.all_receipt_files) && selectedPayment.all_receipt_files.length > 0) {
      return selectedPayment.all_receipt_files.filter(Boolean)
    }

    if (Array.isArray(selectedPayment?.all_receipt_urls) && selectedPayment.all_receipt_urls.length > 0) {
      return selectedPayment.all_receipt_urls.filter(Boolean)
    }

    return []
  }, [selectedPayment])

  const isLastDealItem = (deal?.cart?.cart_items || []).length === 1

  const paymentState = selectedPayment?.state
  const paymentDone = [
    PAYMENT_STATE.DP_CONFIRMED,
    PAYMENT_STATE.CAPTURE,
    PAYMENT_STATE.SETTLEMENT
  ].includes(paymentState)

  const customerReceiptAvailable = deal?.customer_receipt_available === true
  const invoicePrintable = deal?.invoice_printable === true
  const outstandingBalance = Number(deal?.payment_outstanding_balance || 0)
  const hasPendingPayment = paymentsAsc.some(payment => payment?.state === PAYMENT_STATE.PENDING)
  const paymentsPopoverId = `sales-deal-payments-${deal?.id || 'current'}`

  // Owner/Admin may upload payment proof for any accessible deal.
  // Other roles remain creator-only; backend permission is the source of truth.
  const canUploadPaymentReceipt =
    permissions?.can_upload_payment_receipt === true

  // SA hanya upload bukti. Jika payment sebelumnya sudah CF dan outstanding
  // masih ada, backend akan auto-create Payment pending saat upload.
  const canAutoCreatePendingPaymentOnUpload =
    canUploadPaymentReceipt &&
    customerReceiptAvailable &&
    ['waiting_for_payment', 'sourced'].includes(deal?.state) &&
    outstandingBalance > 0 &&
    !hasPendingPayment

  const dealState = deal?.state
  const dealInProgress = !!dealState && ![DEAL_STATE.CANCELLED, DEAL_STATE.FAILED].includes(dealState)

  const isManualTransfer = selectedPayment?.method_type === PAYMENT_METHOD.MANUAL_TRANSFER

  const shouldShowTransferReceiptCard =
    !!selectedPayment &&
    (
      canEditDeal ||
      selectedPaymentReceipts.length > 0 ||
      (
        paymentDone &&
        [
          PAYMENT_METHOD.MANUAL_TRANSFER,
          PAYMENT_METHOD.CASH,
          PAYMENT_METHOD.EDC,
          PAYMENT_METHOD.CREDIT_CARD,
          'edc_bri',
          'edc_bca',
        ].includes(selectedPayment?.method_type)
      )
    )

  const dealInclusions = []
  if (deal?.include_invoice) dealInclusions.push(<li key="include_invoice">Invoice</li>)
  if (deal?.include_polaroid) dealInclusions.push(<li key="include_polaroid">Polaroid</li>)
  if (deal?.include_love_letter) dealInclusions.push(<li key="include_love_letter">Surat Cinta</li>)

  const assistByList = Array.isArray(deal?.assist_by_list)
    ? deal.assist_by_list.map(x => (x || '').toString().trim()).filter(Boolean)
    : []

  const handleEmailInvoice = async (e) => {
    setEmailInvoiceResult(null)

    if (hasPoSkuDeal) {
      setEmailInvoiceResult({
        color: 'warning',
        message: 'Email Invoice to Customer is disabled because this deal contains SKU with prefix PO-.'
      })
      return
    }

    setIsEmailingInvoice(true)
    try {
      const result = await emailInvoice(e, deal)
      setEmailInvoiceResult({
        color: 'success',
        message: result?.message || 'Invoice email sent successfully.'
      })

      if (isHistoryOpen) fetchHistoryList()

    } catch (err) {
      setEmailInvoiceResult({
        color: 'danger',
        message: err?.message || 'Failed to send invoice email.'
      })
    } finally {
      setIsEmailingInvoice(false)
    }
  }

  const handleOpenMultipleEmailInvoice = (e) => {
    e?.preventDefault?.()

    if (!isOwner) return

    setEmailInvoiceResult(null)
    setMultipleEmailInvoiceError(null)

    if (hasPoSkuDeal) {
      setEmailInvoiceResult({
        color: 'warning',
        message: 'Email Invoice to Customer is disabled because this deal contains SKU with prefix PO-.'
      })
      return
    }

    const customerEmail = (deal?.user?.email || '').toString().trim()

    setMultipleInvoiceRecipients(customerEmail ? [customerEmail] : [])
    setNewMultipleInvoiceRecipient('')
    setIsMultipleEmailInvoiceOpen(true)
  }

  const handleAddMultipleInvoiceRecipient = () => {
    const email = newMultipleInvoiceRecipient.trim()

    if (!email) return

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMultipleEmailInvoiceError(`Format email tidak valid: ${email}`)
      return
    }

    const alreadyExists = multipleInvoiceRecipients.some(
      (item) => item.toLowerCase() === email.toLowerCase()
    )

    if (alreadyExists) {
      setMultipleEmailInvoiceError(
        `${email} sudah ada di daftar recipient.`
      )
      return
    }

    setMultipleInvoiceRecipients((current) => [...current, email])
    setNewMultipleInvoiceRecipient('')
    setMultipleEmailInvoiceError(null)
  }

  const handleRemoveMultipleInvoiceRecipient = (email) => {
    setMultipleInvoiceRecipients((current) =>
      current.filter((item) => item !== email)
    )

    setMultipleEmailInvoiceError(null)
  }

  const handleSendMultipleEmailInvoice = async (e) => {
    e?.preventDefault?.()

    if (!isOwner) return

    setMultipleEmailInvoiceError(null)
    setEmailInvoiceResult(null)

    if (multipleInvoiceRecipients.length === 0) {
      setMultipleEmailInvoiceError(
        'Minimal satu email tujuan harus diisi.'
      )
      return
    }

    setIsEmailingInvoice(true)

    try {
      const result = await emailInvoice(
        e,
        deal,
        multipleInvoiceRecipients
      )

      setEmailInvoiceResult({
        color: 'success',
        message:
          result?.message ||
          `Invoice berhasil dikirim ke ${multipleInvoiceRecipients.length} email.`
      })

      setIsMultipleEmailInvoiceOpen(false)

      if (isHistoryOpen) fetchHistoryList()
    } catch (err) {
      setMultipleEmailInvoiceError(
        err?.message || 'Failed to send invoice email.'
      )
    } finally {
      setIsEmailingInvoice(false)
    }
  }

  const buildPrintResult = (result, successMessage) => {
    const warnings = Array.isArray(result?.warnings) ? result.warnings : []

    if (warnings.length === 0) {
      return {
        color: 'success',
        message: successMessage,
      }
    }

    const imageWarnings = warnings.filter(warning => warning?.type?.startsWith('image'))

    return {
      color: 'warning',
      message: `${successMessage} ${imageWarnings.length || warnings.length} image/asset gagal dimuat dan dilewati agar PDF tetap bisa dibuat.`,
    }
  }

  const handlePrintCustomerReceipt = async (e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    setCustomerReceiptResult(null)
    setIsPrintingCustomerReceipt(true)

    try {
      await SalesDealsApi.logPrint({
        dealId: deal.id,
        documentType: 'customer_receipt',
        displayHeaderAndFooter: showPrintHeaderAndFooter,
      })
      if (isHistoryOpen) fetchHistoryList()

      const result = await printCustomerReceipt(null, deal, {
        displayHeaderAndFooter: showPrintHeaderAndFooter,
      })
      setCustomerReceiptResult(buildPrintResult(result, 'Customer Receipt PDF berhasil dibuat.'))
    } catch (error) {
      setCustomerReceiptResult({
        color: 'danger',
        message: extractApiErrorMessage(error, 'Customer Receipt PDF gagal dibuat.'),
      })
    } finally {
      setIsPrintingCustomerReceipt(false)
    }
  }

  const handlePrintInvoice = async (e) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    setPrintInvoiceResult(null)
    setIsPrintingInvoice(true)

    try {
      await SalesDealsApi.logPrint({
        dealId: deal.id,
        documentType: 'invoice',
        displayHeaderAndFooter: showPrintHeaderAndFooter,
      })
      if (isHistoryOpen) fetchHistoryList()

      const result = await printInvoice(null, deal, {
        displayHeaderAndFooter: showPrintHeaderAndFooter,
      })
      setPrintInvoiceResult(buildPrintResult(result, 'Invoice PDF berhasil dibuat.'))
    } catch (error) {
      setPrintInvoiceResult({
        color: 'danger',
        message: extractApiErrorMessage(error, 'Invoice PDF gagal dibuat.'),
      })
    } finally {
      setIsPrintingInvoice(false)
    }
  }

  const handleSaveEdit = async (values, formikBag) => {
    setEditResult(null)
    setIsSavingEdit(true)

    try {
      const cleanedAssistByList = Array.from(
        new Set(
          (values.assist_by_list || [])
            .map(x => (x || '').toString().trim())
            .filter(Boolean)
        )
      )

      if (isAssistOnlyEdit) {
        const response = await updateDeal({ assist_by_list: cleanedAssistByList })

        setEditResult({
          color: 'success',
          message: response?.data?.message || 'Deal assist updated successfully.'
        })

        formikBag.resetForm({
          values: buildEditForm(response?.data?.data || deal)
        })

        setIsEditOpen(false)
        return
      }

      if (hasPoSkuDeal) {
        const missingPoFields = []
        if (!values.po_designer_id) missingPoFields.push('Brand / Designer PO')
        if (!values.po_category_id) missingPoFields.push('Category PO')
        if (!values.po_condition_id) missingPoFields.push('Condition PO')
        if (!(values.po_comes_with || '').toString().trim()) missingPoFields.push('Kelengkapan PO')

        if (missingPoFields.length > 0) {
          setEditResult({
            color: 'danger',
            message: `Mohon lengkapi: ${missingPoFields.join(', ')}.`
          })
          return
        }
      }

      const selectedPaymentMethodOption =
        paymentMethodOptions.find((option) => (option.option_value || option.value) === values.payment_method) || null

      const selectedPaymentMethodValue = selectedPaymentMethodOption?.value || values.payment_method || null
      const selectedPaymentCardType =
        EDC_PAYMENT_METHODS.includes(selectedPaymentMethodValue)
          ? (values.card_type || null)
          : (selectedPaymentMethodOption?.card_type || null)
      const selectedInstallment = selectedPaymentMethodOption?.installment ?? null

      const payload = {
        sales_method: values.sales_method,
        sales_method_note: shouldShowSalesMethodNote(values.sales_method) ? values.sales_method_note : '',
        sales_name_in_invoice: values.sales_name_in_invoice,
        deal_by: values.deal_by,
        assist_by_list: cleanedAssistByList,
        pickup_at: values.pickup_at || null,
        note: values.note,
        include_invoice: !!values.include_invoice,
        include_polaroid: !!values.include_polaroid,
        include_love_letter: !!values.include_love_letter,
      }

      const currentPaymentMethod = deal?.cart?.payment_method || deal?.payment?.method_type || null
      const currentRateId = deal?.cart?.shipping_rate_id ? Number(deal.cart.shipping_rate_id) : null
      const currentInstallment = deal?.cart?.installment ?? null
      const currentCardType =
        EDC_PAYMENT_METHODS.includes(currentPaymentMethod) || currentPaymentMethod === PAYMENT_METHOD.CREDIT_CARD
          ? (deal?.payment?.card_type || null)
          : null
      const selectedRateId = values.rate_id ? Number(values.rate_id) : null

      // Do not resend unchanged checkout fields when editing ordinary Deal data.
      // Backend payment/courier validation must only run for a real checkout change;
      // otherwise editing Assist By after Finance CF can be blocked by paid-payment rules.
      if (selectedPaymentMethodValue !== currentPaymentMethod) {
        payload.payment_method = selectedPaymentMethodValue
      }
      if (selectedRateId !== currentRateId) {
        payload.rate_id = selectedRateId
      }
      if (selectedInstallment !== currentInstallment) {
        payload.installment = selectedInstallment
      }
      if (selectedPaymentCardType !== currentCardType) {
        payload.card_type = selectedPaymentCardType
      }

      if (hasPoSkuDeal) {
        payload.po_designer_id = Number(values.po_designer_id)
        payload.po_category_id = Number(values.po_category_id)
        payload.po_condition_id = Number(values.po_condition_id)
        payload.po_comes_with = (values.po_comes_with || '').toString().trim()
      }

      const response = await updateDeal(payload)

      setEditResult({
        color: 'success',
        message: response?.data?.message || 'Deal updated successfully.'
      })
      formikBag.resetForm({
        values: buildEditForm(response?.data?.data || deal)
      })
      setIsEditOpen(false)
    } catch (err) {
      const latestPermissions = err?.response?.data?.permissions || {}

      if (latestPermissions?.assist_edit_cutoff_locked) {
        setIsEditOpen(false)

        // Refresh permission state without masking the original update error.
        Promise.resolve(fetchDeal()).catch(() => {})
      }

      setEditResult({
        color: 'danger',
        message: extractApiErrorMessage(err, 'Failed to update deal.')
      })
    } finally {
      setIsSavingEdit(false)
    }
  }

  const updateFinalPriceField = (cartItemId, value) => {
    setFinalPriceForm(prev => ({
      ...prev,
      [cartItemId]: sanitizeThousandsInput(value)
    }))
  }

  const resetFinalPriceForm = () => {
    setFinalPriceForm(buildFinalPriceForm(deal))
    setFinalPriceResult(null)
  }

  const handleSaveFinalPrices = async () => {
    setFinalPriceResult(null)

    // Guard the submit handler too, not only the button visibility/disabled state.
    // This prevents stale/open forms from submitting after the deal becomes locked.
    if (isFinalPriceLocked) {
      setFinalPriceResult({
        color: 'warning',
        message: finalPriceLockMessage
      })
      return
    }

    setIsSavingFinalPrice(true)

    try {
      const items = (deal?.cart?.cart_items || []).map((cartItem) => ({
        cart_item_id: cartItem.id,
        final_price: finalPriceForm[cartItem.id]
      }))

      const response = await updateFinalPrices({ items })

      setFinalPriceResult({
        color: 'success',
        message: response?.data?.message || 'Final price updated successfully.'
      })

      setIsFinalPriceOpen(false)
    } catch (err) {
      setFinalPriceResult({
        color: 'danger',
        message: extractApiErrorMessage(err, 'Failed to update final price.')
      })
    } finally {
      setIsSavingFinalPrice(false)
    }
  }

  const customerDetail = deal?.user_detail || deal?.user?.detail
  const customerName =
    customerDetail?.name ||
    [customerDetail?.first_name, customerDetail?.last_name].filter(Boolean).join(' ') ||
    '-'
  const customerPhone = customerDetail?.phone || '-'
  const customerEmail = deal?.user?.email || '-'

  const shouldShowSalesMethodNote = (salesMethod) => {
    return SALES_METHOD_NOTE_REQUIRED_METHODS.includes(salesMethod)
  }

  const getSalesMethodNoteLabel = (salesMethod) => {
    return SALES_METHOD_NOTE_LABEL_BY_METHOD[salesMethod] || 'Sales Method Note'
  }

  return (
    <>
      <Card>
        <div className='p-3'>
          <div>
            <h2 className='mb-3 text-center'>{`Summary (#${deal?.id}) - ${deal?.reference_number}`}</h2>

            <div className='mb-3' style={actionButtonGroupStyle}>
              <Button onClick={fetchDeal} color="info" style={actionButtonStyle}>
                Refresh Data
              </Button>
            </div>

            {/* =========================
                Payment Selector (if multiple)
               ========================= */}
            {paymentsAsc.length > 1 && (
              <div className="mb-3">
                <Label className="font-weight-bold">Pilih Payment</Label>
                <Input
                  type="select"
                  value={selectedPayment?.id || ''}
                  onChange={(e) => setSelectedPaymentId(parseInt(e.target.value, 10))}
                >
                  {paymentsAsc.map((p, idx) => {
                    const label = [
                      `Payment #${idx + 1}`,
                      p?.reference_number ? `(${p.reference_number})` : null,
                      p?.method_type ? paymentMethodLabel(p.method_type, p.card_type) : null,
                      p?.state ? `- ${StringUtil.capitalizeAll(StringUtil.humanize(p.state))}` : null,
                    ].filter(Boolean).join(' ')
                    return (
                      <option key={p.id} value={p.id}>
                        {label}
                      </option>
                    )
                  })}
                </Input>
                <Alert color="secondary" className="mt-2">
                  Total payments pada deal ini: <b>{paymentsAsc.length}</b>
                </Alert>
              </div>
            )}


            {/* =========================
                Manual Transfer Instruction
               ========================= */}
            {!paymentDone && dealInProgress && isManualTransfer && (
              <div className='text-center text-sm flex-col'>
                <p>
                  Silahkan transfer ke BCA no. rek. <b>4232387878</b> a/n <b>PT BERKAH NUSANTARA KAYANA</b>
                </p>
                {paymentIndex && (
                  <p className="text-muted" style={{ marginTop: -6 }}>
                    Ini untuk <b>Payment #{paymentIndex}</b> dari {paymentsAsc.length}
                  </p>
                )}
              </div>
            )}

            {/* Upload receipt for selected payment */}
           {shouldShowTransferReceiptCard && (
<UploadTransferReceiptCard
  deal={deal}
  payment={selectedPayment}
  paymentIndex={paymentIndex}
  totalPayments={paymentsAsc.length}
  fetchDeal={fetchDeal}
  autoCreatePendingPayment={
    selectedPayment?.state !== PAYMENT_STATE.PENDING &&
    canAutoCreatePendingPaymentOnUpload
  }
  onPaymentCreated={(paymentId) => {
    if (paymentId) setSelectedPaymentId(Number(paymentId))
  }}
  canUpload={
    canUploadPaymentReceipt &&
    (
      selectedPayment?.state === PAYMENT_STATE.PENDING ||
      selectedPayment?.state === PAYMENT_STATE.DP_CONFIRMED ||
      canAutoCreatePendingPaymentOnUpload
    )
  }
  lockReason={
    !canUploadPaymentReceipt
      ? (
          permissions?.created_by_me
            ? (editLockReason || 'Deal ini tidak dapat menerima upload payment proof.')
            : 'Payment proof hanya dapat diupload oleh pembuat deal atau Owner/Admin.'
        )
      : (
          selectedPayment?.state !== PAYMENT_STATE.PENDING &&
          !canAutoCreatePendingPaymentOnUpload
            ? 'Payment ini sudah dikonfirmasi Finance.'
            : null
        )
  }
/>
            )}

            {/* Payment Link for non-manual transfer */}
            {!paymentDone &&
              dealInProgress &&
              [PAYMENT_METHOD.MANUAL_TRANSFER, PAYMENT_METHOD.CASH, PAYMENT_METHOD.EDC].includes(selectedPayment?.method_type) === false && (
              <div className='text-center text-sm flex-col'>
                <p className='font-weight-bold'>Payment Link</p>
                <p style={{ wordBreak: 'break-word' }}>{selectedPayment?.redirect_url || '-'}</p>
                {selectedPayment?.redirect_url && (
                  <QRCode className='mb-4' value={selectedPayment.redirect_url} />
                )}
              </div>
            )}

            {/* Summary Table */}
            <Table bordered responsive striped>
              <tbody>
                <tr>
                  <th scope="row" className='text-right'>Customer (Detail)</th>
                  <td>
                    {deal?.user?.id ? (
                      <a
                        href={`/app/sales/users/${deal.user.id}`}
                        className='font-weight-bold'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {customerName}
                      </a>
                    ) : customerName}
                  </td>
                </tr>

                <tr>
                  <th scope="row" className='text-right'>Account Email</th>
                  <td>
                    {deal?.user?.id ? (
                      <a
                        href={`/app/sales/users/${deal.user.id}`}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {customerEmail}
                      </a>
                    ) : customerEmail}
                  </td>
                </tr>

                <tr>
                  <th scope="row" className='text-right'>Phone</th>
                  <td>{customerPhone}</td>
                </tr>

                <tr>
                  <th scope="row" className='text-right'>Final Payment (Deal)</th>
                  <td>{StringUtil.currencyCase(deal?.final_payment)}</td>
                </tr>

                <tr>
                  <th scope="row" className='text-right'>Deal Status</th>
                  <td>{StringUtil.capitalizeAll(StringUtil.humanize(deal?.state))}</td>
                </tr>

                <tr>
                  <th scope="row" className='text-right'>Payment</th>
                  <td>
                    {paymentIndex ? <b>{`#${paymentIndex}/${paymentsAsc.length}`}</b> : '-'}{' '}
                    {selectedPayment?.method_type ? `- ${paymentMethodLabel(selectedPayment.method_type, selectedPayment.card_type)}` : ''}
                    {selectedPayment?.state ? ` (${StringUtil.capitalizeAll(StringUtil.humanize(selectedPayment.state))})` : ''}
                    {selectedPayment?.reference_number ? ` - ${selectedPayment.reference_number}` : ''}
                  </td>
                </tr>

                <tr>
                  <th scope="row" className='text-right'>CF Finance At</th>
                  <td>
                    {selectedPayment?.paid_at
                      ? TimeUtil.format(selectedPayment.paid_at, 'YYYY-MM-DD HH:mm:ss')
                      : '-'}
                  </td>
                </tr>

                <tr>
                  <th scope="row" className='text-right'>Card Type</th>
                  <td>
                    {paymentCardTypeLabel(
                      selectedPayment?.method_type,
                      selectedPayment?.card_type
                    )}
                  </td>
                </tr>

                <tr>
                  <th scope="row" className='text-right'>Kurir</th>
                  <td>
                    {[
                      deal?.shipment?.courier_detail?.logistic?.name,
                      deal?.shipment?.courier_detail?.rate?.name
                    ].filter(str => !!str).join(' - ') || '-'}
                  </td>
                </tr>

                <tr>
                  <th scope="row" className='text-right'>Pickup Date</th>
                  <td>{deal?.pickup_at ? TimeUtil.format(deal.pickup_at, 'DD-MM-YYYY') : '-'}</td>
                </tr>

                <tr>
                  <th scope="row" className='text-right'>Down Payment</th>
                  <td>{deal?.down_payment ? StringUtil.currencyCase(deal.down_payment) : '-'}</td>
                </tr>

                {shouldShowDownPaymentProofRow && (
                  <tr>
                    <th scope="row" className='text-right'>DP Proof</th>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {hasExistingDownPaymentProofs && downPaymentProofUrls.map((url, idx) => {
                          const isPdf = isPdfUrl(url)
                          const isBroken = !!brokenDownPaymentProofMap[idx]

                          return (
                            <div key={`dp_proof_${idx}`}>
                              {isPdf ? (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View DP Proof #{idx + 1} (PDF)
                                </a>
                              ) : !isBroken ? (
                                <>
                                  <div style={{ fontSize: 12, marginBottom: 4 }}>
                                    DP Proof #{idx + 1}
                                  </div>
                                  <img
                                    src={url}
                                    alt={`DP Proof ${idx + 1}`}
                                    onError={() => {
                                      setBrokenDownPaymentProofMap(prev => ({ ...prev, [idx]: true }))
                                    }}
                                    style={{
                                      width: '100%',
                                      maxHeight: 300,
                                      objectFit: 'contain',
                                      border: '1px solid #ddd',
                                      borderRadius: 6
                                    }}
                                  />
                                </>
                              ) : (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Open DP Proof #{idx + 1}
                                </a>
                              )}
                            </div>
                          )
                        })}

                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {isAssistOnlyEdit && (
              <Alert color='warning' className='mb-3'>
                Deal status saat ini hanya mengizinkan edit field Assist By.
              </Alert>
            )}

            {canOpenEditDeal && (
              <Card className="mb-3">
                <CardBody>
                  <Formik
                    enableReinitialize
                    initialValues={buildEditForm(deal)}
                    onSubmit={handleSaveEdit}
                  >
                    {(formikBag) => {
                      const selectedPaymentMethodOption =
                        paymentMethodOptions.find(
                          (option) => (option.option_value || option.value) === formikBag.values.payment_method
                        ) || null

                      const selectedPaymentMethodValue =
                        selectedPaymentMethodOption?.value || formikBag.values.payment_method || ''

                      const shouldShowCardTypeField =
                        EDC_PAYMENT_METHODS.includes(selectedPaymentMethodValue) ||
                        selectedPaymentMethodValue === PAYMENT_METHOD.CREDIT_CARD

                      const poDesignerOptions =
                        poMeta.designers.length > 0
                          ? poMeta.designers
                          : (firstPoProduct?.designer ? [firstPoProduct.designer] : [])

                      const poCategoryOptions =
                        poMeta.categories.length > 0
                          ? poMeta.categories
                          : (firstPoProduct?.category ? [firstPoProduct.category] : [])

                      const poConditionOptions =
                        poMeta.conditions.length > 0
                          ? poMeta.conditions
                          : (firstPoProduct?.condition ? [firstPoProduct.condition] : [])

                      const selectedPoDesigner =
                        poDesignerOptions.find((designer) => String(designer.id) === String(formikBag.values.po_designer_id)) ||
                        firstPoProduct?.designer ||
                        null

                      const selectedPoCategory =
                        poCategoryOptions.find((category) => String(category.id) === String(formikBag.values.po_category_id)) ||
                        firstPoProduct?.category ||
                        null

                      const poProductNamePreview = buildPoPlaceholderNamePreview(
                        firstPoProduct,
                        selectedPoDesigner,
                        selectedPoCategory
                      )

                      const poProductSkuPreview = buildPoPlaceholderSkuPreview(
                        firstPoProduct,
                        selectedPoDesigner,
                        selectedPoCategory
                      )

                      return (
                        <>
                          <div style={actionButtonGroupStyle}>
                            <Button
                              type="button"
                              color="warning"
                              style={actionButtonStyle}
                              onClick={() => setIsEditOpen(!isEditOpen)}
                          >
                            {isEditOpen
                              ? (isAssistOnlyEdit ? 'Close Edit Assist' : 'Close Edit Deal')
                              : (isAssistOnlyEdit ? 'Edit Assist' : 'Edit Deal')}
                          </Button>

                          {isEditOpen && (
                            <Button
                              type="button"
                              color="secondary"
                              outline
                              style={actionButtonStyle}
                              onClick={() => {
                                formikBag.resetForm()
                                setEditResult(null)
                              }}
                              disabled={isSavingEdit}
                            >
                              Reset Form
                            </Button>
                          )}
                        </div>

                        {editResult && (
                          <Alert color={editResult.color} className="mt-3 mb-0">
                            {editResult.message}
                          </Alert>
                        )}

                        <Collapse isOpen={isEditOpen}>
                          <Row className="mt-3">
                            {!isAssistOnlyEdit && (
                              <>
                                <Col md="6" className="mb-3">
                                  <InputField
                                type='select'
                                name='sales_method'
                                label='Sales Method'
                                required={ true }
                              >
                                <option value=''>-- pilih sales method --</option>
                                <InputSelectOptions options={ SALES_METHOD } keyPrefix={ 'sales_method_edit' } />
                              </InputField>

                              {shouldShowSalesMethodNote(formikBag.values.sales_method) && (
                                <Col md="6" className="mb-3">
                                  <InputField
                                    type='text'
                                    name='sales_method_note'
                                    label={getSalesMethodNoteLabel(formikBag.values.sales_method)}
                                    required={ true }
                                    placeholder='Contoh: Nama influencer / akun Instagram'
                                  />
                                </Col>
                              )}

                            </Col>

                            <Col md="6" className="mb-3">
                              <SalesAliasSelectSearchField
                                name='sales_name_in_invoice'
                                label='Nama Sales di Invoice'
                                required={ true }
                              />
                            </Col>

                            <Col md="6" className="mb-3">
                              <SalesNameSelectSearchField
                                name='deal_by'
                                label='Deal By'
                                required={ true }
                              />
                            </Col>

                            <Col md="6" className="mb-3">
                              <Label>Payment Method</Label>
                              <Input
                                type="select"
                                value={formikBag.values.payment_method || ''}
                                onChange={(e) => {
                                  const nextOptionValue = e.target.value
                                  const nextOption = paymentMethodOptions.find(
                                    (option) => (option.option_value || option.value) === nextOptionValue
                                  )

                                  formikBag.setFieldValue('payment_method', nextOptionValue)
                                  if (
                                    !EDC_PAYMENT_METHODS.includes(nextOption?.value) &&
                                    nextOption?.value !== PAYMENT_METHOD.CREDIT_CARD
                                  ) {
                                    formikBag.setFieldValue('card_type', '')
                                  }

                                  if (nextOption?.value === PAYMENT_METHOD.CREDIT_CARD) {
                                    formikBag.setFieldValue('card_type', nextOption?.card_type || '')
                                  }

                                }}
                                disabled={paymentsAsc.length > 1}
                              >
                                <option value="">-- pilih payment method --</option>
                                {paymentMethodOptions.map((option) => (
                                  <option key={option.option_value || option.value} value={option.option_value || option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </Input>
                              {paymentsAsc.length > 1 && (
                                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                  Payment Method hanya bisa diubah jika deal masih punya 1 payment term.
                                </div>
                              )}
                            </Col>

                            <Col md="6" className="mb-3">
                              <Label>Courier</Label>
                              <Input
                                type="select"
                                value={formikBag.values.rate_id || ''}
                                onChange={(e) => formikBag.setFieldValue('rate_id', e.target.value)}
                                disabled={isLoadingCourierOptions || paymentsAsc.length > 1}
                              >
                                <option value="">-- pilih courier --</option>
                                {courierOptions.map((option) => (
                                  <option key={`${option?.rate?.id}_${option?.rate?.name}`} value={String(option?.rate?.id || '')}>
                                    {courierOptionLabel(option)}
                                  </option>
                                ))}
                              </Input>
                              {isLoadingCourierOptions && (
                                <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                                  Loading courier options...
                                </div>
                              )}
                            </Col>

                            {shouldShowCardTypeField && (
                              <Col md="6" className="mb-3">
                                <Label>Card Type</Label>
                                <Input
                                  type="select"
                                  value={formikBag.values.card_type || ''}
                                  onChange={(e) => formikBag.setFieldValue('card_type', e.target.value)}
                                  disabled={selectedPaymentMethodValue === PAYMENT_METHOD.CREDIT_CARD}
                                >
                                  <option value="">-- pilih card type --</option>
                                  {CARD_TYPE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Input>
                              </Col>
                            )}

                            {hasPoSkuDeal && (
                              <Col xs="12" className="mb-3">
                                <Alert color="info" className="mb-3">
                                  Detail PO Product hanya muncul untuk deal dengan SKU prefix <b>PO-</b>.
                                </Alert>

                                {isPoMetaLoading && (
                                  <Alert color="secondary" className="mb-3">
                                    Loading PO product options...
                                  </Alert>
                                )}

                                <Row>
                                  <Col md="6" className="mb-3">
                                    <Label>Brand / Designer (PO)</Label>
                                    <Input
                                      type="select"
                                      value={formikBag.values.po_designer_id || ''}
                                      onChange={(e) => formikBag.setFieldValue('po_designer_id', e.target.value)}
                                      disabled={isPoMetaLoading}
                                    >
                                      <option value="">-- pilih brand --</option>
                                      {poDesignerOptions.map((designer) => (
                                        <option key={designer.id} value={String(designer.id)}>
                                          {designer.code ? `${designer.code} - ${designer.name}` : designer.name}
                                        </option>
                                      ))}
                                    </Input>
                                  </Col>

                                  <Col md="6" className="mb-3">
                                    <Label>Category (PO)</Label>
                                    <Input
                                      type="select"
                                      value={formikBag.values.po_category_id || ''}
                                      onChange={(e) => formikBag.setFieldValue('po_category_id', e.target.value)}
                                      disabled={isPoMetaLoading}
                                    >
                                      <option value="">-- pilih category --</option>
                                      {poCategoryOptions.map((category) => (
                                        <option key={category.id} value={String(category.id)}>
                                          {category.code ? `${category.code} - ${category.name}` : category.name}
                                        </option>
                                      ))}
                                    </Input>
                                  </Col>

                                  <Col md="6" className="mb-3">
                                    <Label>Condition (PO)</Label>
                                    <Input
                                      type="select"
                                      value={formikBag.values.po_condition_id || ''}
                                      onChange={(e) => formikBag.setFieldValue('po_condition_id', e.target.value)}
                                      disabled={isPoMetaLoading}
                                    >
                                      <option value="">-- pilih condition --</option>
                                      {poConditionOptions.map((condition) => (
                                        <option key={condition.id} value={String(condition.id)}>
                                          {condition.name}
                                        </option>
                                      ))}
                                    </Input>
                                  </Col>

                                  <Col md="6" className="mb-3">
                                    <Label>SKU PO Product</Label>
                                    <Input
                                      type="text"
                                      value={poProductSkuPreview}
                                      disabled
                                    />
                                  </Col>

                                  <Col md="6" className="mb-3">
                                    <Label>Nama PO Product</Label>
                                    <Input
                                      type="text"
                                      value={poProductNamePreview}
                                      disabled
                                    />
                                  </Col>

                                  <Col xs="12" className="mb-3">
                                    <Label>Kelengkapan / Comes With (PO)</Label>
                                    <Input
                                      type="textarea"
                                      rows="3"
                                      value={formikBag.values.po_comes_with || ''}
                                      onChange={(e) => formikBag.setFieldValue('po_comes_with', e.target.value)}
                                      placeholder="Contoh: box, dustbag, strap, receipt..."
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            )}

                            <Col md="6" className="mb-3">
                              <Label>Pickup Date</Label>
                              <Input
                                type="date"
                                value={formikBag.values.pickup_at || ''}
                                onChange={(e) => formikBag.setFieldValue('pickup_at', e.target.value)}
                              />
                            </Col>

                              </>
                            )}

                            <Col xs="12" className="mb-3">
                              <FieldArray
                                name='assist_by_list'
                                render={(arrayHelpers) => {
                                  const list = formikBag.values.assist_by_list || ['']

                                  return (
                                    <div className='mt-2'>
                                      <Label style={{ fontWeight: 700 }}>Assist By (Array)</Label>

                                      {list.map((_, idx) => {
                                        const fieldName = `assist_by_list.${idx}`
                                        const isFirst = idx === 0

                                        return (
                                          <div
                                            key={fieldName}
                                            style={{
                                              border: '1px solid #eee',
                                              borderRadius: 6,
                                              padding: 12,
                                              marginBottom: 10,
                                              background: '#fafafa',
                                            }}
                                          >
                                            <SalesNameSelectSearchField
                                              name={fieldName}
                                              label={`Assist By ${idx + 1}`}
                                              required={isFirst}
                                            />

                                            {!isFirst && (
                                              <Button
                                                type='button'
                                                color='danger'
                                                size='sm'
                                                onClick={() => {
                                                  arrayHelpers.remove(idx)

                                                  const next = (formikBag.values.assist_by_list || []).filter((_, i) => i !== idx)
                                                  if (next.length === 0) arrayHelpers.push('')
                                                }}
                                              >
                                                Hapus Assist
                                              </Button>
                                            )}
                                          </div>
                                        )
                                      })}

                                      <Button
                                        type='button'
                                        color='secondary'
                                        onClick={() => arrayHelpers.push('')}
                                      >
                                        + Tambah Assist
                                      </Button>
                                    </div>
                                  )
                                }}
                              />
                            </Col>

                            {!isAssistOnlyEdit && (
                              <>
                                <Col xs="12" className="mb-3">
                                  <Label>Catatan Order</Label>
                              <Input
                                type="textarea"
                                rows="4"
                                value={formikBag.values.note || ''}
                                onChange={(e) => formikBag.setFieldValue('note', e.target.value)}
                              />
                            </Col>

                            <Col md="4" className="mb-2">
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={!!formikBag.values.include_invoice}
                                  onChange={(e) => formikBag.setFieldValue('include_invoice', e.target.checked)}
                                />{' '}
                                Include Invoice
                              </Label>
                            </Col>

                            <Col md="4" className="mb-2">
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={!!formikBag.values.include_polaroid}
                                  onChange={(e) => formikBag.setFieldValue('include_polaroid', e.target.checked)}
                                />{' '}
                                Include Polaroid
                              </Label>
                            </Col>
                                <Col md="4" className="mb-2">
                                  <Label check>
                                    <Input
                                      type="checkbox"
                                      checked={!!formikBag.values.include_love_letter}
                                      onChange={(e) => formikBag.setFieldValue('include_love_letter', e.target.checked)}
                                    />{' '}
                                    Include Surat Cinta
                                  </Label>
                                </Col>
                              </>
                            )}
                          </Row>

                          <div className='mt-3' style={actionButtonGroupStyle}>
                            <Button
                              type="button"
                              color="primary"
                              style={actionButtonStyle}
                              onClick={formikBag.submitForm}
                              disabled={isSavingEdit}
                            >
                              {isSavingEdit ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </div>
                        </Collapse>
                      </>
                    )
                  }}
                </Formik>
              </CardBody>
            </Card>
          )}

            {isDealInAssistOnlyState &&
              !permissions?.assist_edit_cutoff_locked &&
              !permissions?.assist_edit_cutoff_bypassed &&
              permissions?.assist_edit_cutoff_at && (
                <Alert color='info' className='mb-3'>
                  <div className='font-weight-bold mb-1'>
                    {permissions?.assist_edit_temporary_blackout
                      ? 'Monthly Cut Off Edit Assist By'
                      : 'Assist By Locked'}
                  </div>

                  <div>
                    Jika deal sudah lunas, Assist By dapat diedit sampai{' '}
                    {TimeUtil.format(
                      permissions.assist_edit_cutoff_at,
                      'DD/MM/YYYY HH:mm'
                    )}{' '}
                    WIB.
                  </div>

                  <div className='mt-1'>
                    Setelah waktu cut off tersebut terlewati, Assist By tetap terkunci untuk deal ini dan tidak dibuka kembali.
                  </div>
                </Alert>
              )}

            {isDealInAssistOnlyState && permissions?.assist_edit_cutoff_bypassed && (
              <Alert color='info' className='mb-3'>
                Owner/Admin dapat mengubah Assist By meskipun monthly cut off deal ini sudah lewat.
              </Alert>
            )}

            {!canOpenEditDeal && editLockReason && (
              <Alert
                color={(financeLocked || permissions?.assist_edit_cutoff_locked) ? 'warning' : 'secondary'}
                className='mb-3'
              >
                {permissions?.assist_edit_cutoff_locked && (
                  <div className='font-weight-bold mb-1'>
                    Monthly Cut Off Edit Assist By
                  </div>
                )}
                {editLockReason}
              </Alert>
            )}

            {canEditDeal && !isDealInAssistOnlyState && (
              <Card className="mb-3">
                <CardBody>
                  {/* Explain why Final Price editing is disabled for this deal. */}
                  {isFinalPriceLocked && (
                    <Alert color="warning" className="mb-3">
                      {finalPriceLockMessage}
                    </Alert>
                  )}

                  <div style={actionButtonGroupStyle}>
                    <Button
                      color="warning"
                      style={actionButtonStyle}
                      onClick={() => setIsFinalPriceOpen(!isFinalPriceOpen)}
                      // Disable the entry point whenever gateway or sourcing rules lock Final Price.
                      disabled={isFinalPriceLocked}
                      title={isFinalPriceLocked ? finalPriceLockMessage : undefined}
                    >
                      {isFinalPriceLocked
                        ? 'Edit Final Price'
                        : isFinalPriceOpen
                          ? 'Close Final Price Form'
                          : 'Edit Final Price'}
                    </Button>

                    {/* Do not expose reset controls while Final Price is locked. */}
                    {isFinalPriceOpen && !isFinalPriceLocked && (
                      <Button
                        color="secondary"
                        outline
                        style={actionButtonStyle}
                        onClick={resetFinalPriceForm}
                        disabled={isSavingFinalPrice}
                      >
                        Reset Final Price
                      </Button>
                    )}
                  </div>

                  {finalPriceResult && (
                    <Alert color={finalPriceResult.color} className="mt-3 mb-0">
                      {finalPriceResult.message}
                    </Alert>
                  )}

                  {/* Keep the edit form closed if the deal becomes locked while the page is open. */}
                  <Collapse isOpen={isFinalPriceOpen && !isFinalPriceLocked}>
                    <div className="mt-3">
                      <Alert color="info">
                        Mengubah final price akan menghitung ulang total deal dan menyesuaikan payment term yang masih pending.
                      </Alert>

                      <Table bordered responsive>
                        <thead>
                          <tr>
                            <th>SKU</th>
                            <th>Product</th>
                            <th>Open Price</th>
                            <th>Current Final Price</th>
                            <th>New Final Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(deal?.cart?.cart_items || []).map((cartItem) => (
                            <tr key={cartItem?.id}>
                              <td>{cartItem?.product?.sku || '-'}</td>
                              <td>{salesDealProductDisplayName(cartItem?.product)}</td>
                              <td>{StringUtil.currencyCase(cartItem?.product?.selling_price || 0)}</td>
                              <td>{StringUtil.currencyCase(cartItem?.price || 0)}</td>
                              <td style={{ minWidth: 220 }}>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  value={formatThousandsInput(finalPriceForm[cartItem.id])}
                                  onChange={(e) => updateFinalPriceField(cartItem.id, e.target.value)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>

                      <div className='mt-3' style={actionButtonGroupStyle}>
                        <Button
                          color="primary"
                          style={actionButtonStyle}
                          onClick={handleSaveFinalPrices}
                          disabled={isSavingFinalPrice}
                        >
                          {isSavingFinalPrice ? 'Saving...' : 'Save Final Price'}
                        </Button>
                      </div>
                    </div>
                  </Collapse>
                </CardBody>
              </Card>
            )}
{/* Payment history inline collapsible */}
{paymentsAsc.length > 0 && (
  <div className="mb-3">
    <div className="text-right">
      <Button
        color="secondary"
        outline
        size="sm"
        onClick={() => setIsPaymentHistoryOpen((prev) => !prev)}
        aria-expanded={isPaymentHistoryOpen}
      >
        {isPaymentHistoryOpen ? 'Tutup Daftar Payments' : 'Daftar Payments'} ({paymentsAsc.length})
      </Button>
    </div>

    <Collapse isOpen={isPaymentHistoryOpen}>
      <Card className="mt-2">
        <CardBody style={{ padding: 0 }}>
          <div
            style={{
              padding: '12px 16px',
              fontWeight: 700,
              borderBottom: '1px solid #e9ecef'
            }}
          >
            Daftar Payments
          </div>

          {paymentsAsc.map((p, idx) => (
            <div
              key={p?.id || idx}
              style={{
                padding: '12px 16px',
                borderBottom:
                  idx === paymentsAsc.length - 1
                    ? 'none'
                    : '1px solid #e9ecef'
              }}
            >
              <div style={{ fontWeight: 700 }}>
                Payment #{idx + 1} · {StringUtil.currencyCase(p?.amount || 0)}
              </div>
              <div>{p?.reference_number || '-'}</div>
              <div>
                {p?.method_type
                  ? paymentMethodLabel(p.method_type, p.card_type)
                  : '-'}
                {' · '}
                {p?.state
                  ? StringUtil.capitalizeAll(StringUtil.humanize(p.state))
                  : '-'}
              </div>
              <div>
                CF Finance:{' '}
                {p?.paid_at
                  ? TimeUtil.format(p.paid_at, 'YYYY-MM-DD HH:mm:ss')
                  : 'Belum dikonfirmasi'}
              </div>
            </div>
          ))}
        </CardBody>
      </Card>
    </Collapse>
  </div>
)}

            {canCancelDeal && (
              <div className='mt-3' style={actionButtonGroupStyle}>
                <Button
                  onClick={() => {
                    setCancelResult(null)
                    setIsCancelConfirmOpen(true)
                  }}
                  color="danger"
                  style={actionButtonStyle}
                >
                  Cancel
                </Button>
              </div>
            )}
            {!canCancelDeal && cancelLockReason && deal?.state !== DEAL_STATE.CANCELLED && (
              <Alert color="secondary" className="mt-3 mb-0">
                Cancel Deal: {cancelLockReason}
              </Alert>
            )}
          </div>
        </div>
      </Card>

      <Card>
        <div className='p-3'>
          <div className='mb-3'>
            <Label check className='mb-0'>
              <Input
                type='checkbox'
                checked={showPrintHeaderAndFooter}
                onChange={(event) => setShowPrintHeaderAndFooter(event.target.checked)}
              />{' '}
              Show Header & Footer (Invoice & Customer Receipt)
            </Label>
            <div className='text-muted' style={{ fontSize: '12px', marginTop: '4px' }}>
              Hanya memengaruhi PDF dari tombol Print di bawah. Pagination, PAID stamp, dan isi dokumen tetap mengikuti layout yang sama.
            </div>
          </div>

          <div className='mb-3' style={actionButtonGroupStyle}>
            <Button
              color="info"
              style={actionButtonStyle}
              onClick={handlePrintCustomerReceipt}
              disabled={!customerReceiptAvailable || isPrintingCustomerReceipt}
              title={
                customerReceiptAvailable
                  ? undefined
                  : 'Customer Receipt aktif setelah minimal satu payment dikonfirmasi Finance.'
              }
            >
              {isPrintingCustomerReceipt ? 'Preparing Customer Receipt...' : 'Print Customer Receipt'}
            </Button>

            <Button
              color="success"
              style={actionButtonStyle}
              onClick={handlePrintInvoice}
              disabled={!invoicePrintable || isPrintingInvoice}
              title={
                invoicePrintable
                  ? undefined
                  : 'Invoice baru tersedia setelah payment lunas / capture / settlement.'
              }
            >
              {isPrintingInvoice ? 'Preparing Invoice...' : 'Print Invoice'}
            </Button>

            <Button
              color="primary"
              style={actionButtonStyle}
              onClick={handleEmailInvoice}
              disabled={isEmailingInvoice || hasPoSkuDeal || !invoicePrintable}
              title={
                !invoicePrintable
                  ? 'Invoice baru tersedia setelah payment lunas / capture / settlement.'
                  : (hasPoSkuDeal ? 'Disabled for PO deal' : undefined)
              }
            >
              {isEmailingInvoice
                ? 'Sending Invoice...'
                : hasPoSkuDeal
                  ? 'Email Invoice Disabled for PO Deal'
                  : 'Email Invoice to Customer'}
            </Button>
            {isOwner && (
              <Button
                color="primary"
                outline
                style={actionButtonStyle}
                onClick={handleOpenMultipleEmailInvoice}
                disabled={isEmailingInvoice || hasPoSkuDeal || !invoicePrintable}
                title={
                  !invoicePrintable
                    ? 'Invoice baru tersedia setelah payment lunas / capture / settlement.'
                    : (hasPoSkuDeal ? 'Disabled for PO deal' : undefined)
                }
              >
                Sent Invoice Multiple Email Customer
              </Button>
            )}
            <Button
              color="info"
              style={actionButtonStyle}
              onClick={() => setIsDetailOpen(!isDetailOpen)}
            >
              Lihat Detail
            </Button>

            <Button
              color="info"
              style={actionButtonStyle}
              onClick={() => setIsHistoryOpen(!isHistoryOpen)}
            >
              Lihat History
            </Button>
          </div>

          {customerReceiptResult && (
            <Alert color={customerReceiptResult.color} className="mb-3">
              {customerReceiptResult.message}
            </Alert>
          )}

          {printInvoiceResult && (
            <Alert color={printInvoiceResult.color} className="mb-3">
              {printInvoiceResult.message}
            </Alert>
          )}

          {hasPoSkuDeal && (
            <Alert color="warning" className="mb-3">
              Email Invoice to Customer dinonaktifkan karena deal ini mengandung SKU prefix <b>PO-</b>.
            </Alert>
          )}

          {emailInvoiceResult && (
            <Alert color={emailInvoiceResult.color} className="mb-3">
              {emailInvoiceResult.message}
            </Alert>
          )}

          <Modal
            isOpen={isOwner && isMultipleEmailInvoiceOpen}
            toggle={() => {
              if (!isEmailingInvoice) {
                setIsMultipleEmailInvoiceOpen(false)
              }
            }}
          >
            <ModalHeader
              toggle={() => {
                if (!isEmailingInvoice) {
                  setIsMultipleEmailInvoiceOpen(false)
                }
              }}
            >
              Send Invoice to Multiple Emails
            </ModalHeader>

            <ModalBody>
              <p className="text-muted mb-3">
                Tambahkan satu atau beberapa email tujuan untuk pengiriman invoice.
              </p>

              {multipleEmailInvoiceError && (
                <Alert color="danger">
                  {multipleEmailInvoiceError}
                </Alert>
              )}

              <Label className="mb-2">
                Recipients
              </Label>

              {multipleInvoiceRecipients.length === 0 && (
                <Alert color="warning">
                  Belum ada email tujuan.
                </Alert>
              )}

              {multipleInvoiceRecipients.map((email) => (
                <div
                  key={email}
                  className="d-flex align-items-center mb-2"
                  style={{ gap: '8px' }}
                >
                  <Input
                    type="email"
                    value={email}
                    readOnly
                  />

                  <Button
                    color="danger"
                    outline
                    type="button"
                    disabled={isEmailingInvoice}
                    onClick={() =>
                      handleRemoveMultipleInvoiceRecipient(email)
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}

              <hr />

              <Label>
                Add Email
              </Label>

              <div
                className="d-flex align-items-center"
                style={{ gap: '8px' }}
              >
                <Input
                  type="email"
                  placeholder="example@email.com"
                  value={newMultipleInvoiceRecipient}
                  disabled={isEmailingInvoice}
                  onChange={(e) => {
                    setNewMultipleInvoiceRecipient(e.target.value)
                    setMultipleEmailInvoiceError(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddMultipleInvoiceRecipient()
                    }
                  }}
                />

                <Button
                  color="secondary"
                  type="button"
                  disabled={
                    isEmailingInvoice ||
                    !newMultipleInvoiceRecipient.trim()
                  }
                  onClick={handleAddMultipleInvoiceRecipient}
                >
                  Add
                </Button>
              </div>

              <small className="text-muted">
                Email customer otomatis dimasukkan sebagai recipient awal dan bisa dihapus jika tidak diperlukan.
              </small>
            </ModalBody>

            <ModalFooter>
              <Button
                color="secondary"
                type="button"
                disabled={isEmailingInvoice}
                onClick={() =>
                  setIsMultipleEmailInvoiceOpen(false)
                }
              >
                Cancel
              </Button>

              <Button
                color="primary"
                type="button"
                disabled={
                  isEmailingInvoice ||
                  multipleInvoiceRecipients.length === 0
                }
                onClick={handleSendMultipleEmailInvoice}
              >
                {isEmailingInvoice
                  ? 'Sending Invoice...'
                  : `Send to ${multipleInvoiceRecipients.length} Email${
                      multipleInvoiceRecipients.length > 1 ? 's' : ''
                    }`}
              </Button>
            </ModalFooter>
          </Modal>

          <Collapse isOpen={isDetailOpen}>
            <Card className='mb-3'>
              <CardBody>
                <h2 className='mb-3 text-center'>Detail</h2>
                <ListGroup className='text-sm'>
                  <ListGroupItem>Voucher: {deal?.voucher_code || '-'}</ListGroupItem>
                  <ListGroupItem>Sales Method: {deal?.sales_method || '-'}</ListGroupItem>
                  {deal?.sales_method_note && (
                    <ListGroupItem>
                      Sales Method Note: {deal.sales_method_note}
                    </ListGroupItem>
                  )}
                  <ListGroupItem>Nama Sales di Invoice: {deal?.sales_name_in_invoice || '-'}</ListGroupItem>
                  <ListGroupItem>Deal By: {deal?.deal_by || '-'}</ListGroupItem>

                  <ListGroupItem>
                    Assist By:&nbsp;
                    {assistByList.length > 0 ? (
                      <ul style={{ paddingLeft: 16, marginTop: 6, marginBottom: 0 }}>
                        {assistByList.map((name, i) => <li key={`${name}_${i}`}>{name}</li>)}
                      </ul>
                    ) : (
                      <span>{deal?.assist_by || '-'}</span>
                    )}
                  </ListGroupItem>

                  <ListGroupItem>
                    Catatan Order:<br /><span style={{ whiteSpace: 'pre-wrap' }}>{deal?.note || '-'}</span>
                  </ListGroupItem>

                  {dealInclusions.length > 0 && (
                    <ListGroupItem>
                      Include:
                      <ul style={{ paddingLeft: '14px' }}>
                        {dealInclusions}
                      </ul>
                    </ListGroupItem>
                  )}
                </ListGroup>

                <h2 className='my-3 text-center'>Produk yang Dibeli</h2>
                <div>
                  {(deal?.cart?.cart_items || []).map((cart_item) => {
                    const inclusionsListItems = []

                    if (cart_item?.include_authenticity_check) {
                      inclusionsListItems.push(
                        <li key={`auth_${cart_item?.id || cart_item?.product?.sku}`}>
                          Authentication
                          <ul style={{ paddingLeft: '14px' }}>
                            {[cart_item?.authenticator, cart_item?.authenticity_check_note]
                              .filter(Boolean)
                              .map((str, i) => (
                                <li key={`auth_note_${cart_item?.id || cart_item?.product?.sku}_${i}`}>{str}</li>
                              ))}
                          </ul>
                        </li>
                      )
                    }

                    const imgSrc = cart_item?.product?.summary_index?.direct_photos?.[0]
                    const itemKey = cart_item?.id || cart_item?.product?.sku
                    const isPoItem = isPoSkuProduct(cart_item?.product)
                    const inventoryHref = salesDealProductInventoryHref(cart_item?.product)

                    return (
                      <Container className="my-4" key={itemKey}>
                        <Row className="align-items-center">
                          <Col xs="12" md="6" className="text-center mb-3 mb-md-0">
                            {imgSrc ? (
                              <img
                                style={{ width: '100%' }}
                                alt={salesDealProductDetailTitle(cart_item?.product)}
                                src={imgSrc}
                              />
                            ) : (
                              <div className="text-muted">No image</div>
                            )}
                          </Col>

                          <Col xs="12" md="6">
                            <p>
                              {inventoryHref ? (
                                <a
                                  href={inventoryHref}
                                  className='font-weight-bold'
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  {salesDealProductDetailTitle(cart_item?.product)}
                                </a>
                              ) : salesDealProductDetailTitle(cart_item?.product)}
                            </p>
                            <ListGroup className='text-sm'>
                              <ListGroupItem>
                                SKU:{' '}
                                {inventoryHref ? (
                                  <a
                                    href={inventoryHref}
                                    className='font-weight-bold'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                  >
                                    {cart_item?.product?.sku}
                                  </a>
                                ) : cart_item?.product?.sku}
                              </ListGroupItem>
                              <ListGroupItem>Consignor / Seller: {salesDealProductConsignorName(cart_item?.product)}</ListGroupItem>
                              <ListGroupItem>Selling Price (HJ): {salesDealProductCurrencyLabel(cart_item?.product?.selling_price)}</ListGroupItem>
                              {salesDealProductBuyingPriceVisible(cart_item?.product) && (
                                <ListGroupItem>HM Buying Price: {salesDealProductCurrencyLabel(cart_item?.product?.buying_price)}</ListGroupItem>
                              )}
                              <ListGroupItem>Qty: {cart_item?.quantity || 1}</ListGroupItem>
                              <ListGroupItem>Harga Satuan: {StringUtil.currencyCase(cart_item?.price)}</ListGroupItem>
                              <ListGroupItem>Subtotal: {StringUtil.currencyCase((Number(cart_item?.price || 0) * Number(cart_item?.quantity || 1)))}</ListGroupItem>

                              {isPoItem && (
                                <>
                                  <ListGroupItem>
                                    Nama PO Product:<br />{cart_item?.product?.name || '-'}
                                  </ListGroupItem>
                                  <ListGroupItem>
                                    Brand / Designer:<br />{getPoProductDesignerName(cart_item?.product)}
                                  </ListGroupItem>
                                  <ListGroupItem>
                                    Category:<br />{getPoProductCategoryName(cart_item?.product)}
                                  </ListGroupItem>
                                  <ListGroupItem>
                                    Condition:<br />{getPoProductConditionName(cart_item?.product)}
                                  </ListGroupItem>
                                  <ListGroupItem>
                                    Kelengkapan / Comes With:<br />{getPoProductComesWith(cart_item?.product)}
                                  </ListGroupItem>
                                  <ListGroupItem>
                                    Catatan Order (Deal):<br /><span style={{ whiteSpace: 'pre-wrap' }}>{deal?.note || '-'}</span>
                                  </ListGroupItem>
                                </>
                              )}

                              {inclusionsListItems.length > 0 && (
                                <ListGroupItem>
                                  Include:
                                  <ul style={{ paddingLeft: '14px' }}>
                                    {inclusionsListItems}
                                  </ul>
                                </ListGroupItem>
                              )}

                              {cart_item?.note && (
                                <ListGroupItem>
                                  Catatan Item:<br />{cart_item.note}
                                </ListGroupItem>
                              )}
                            </ListGroup>

                            {canCancelItem &&
                              (isLastDealItem || isDealItemCancellationUiSupported(cart_item)) && (
                              <Button
                                color="danger"
                                outline
                                type="button"
                                className="mt-3"
                                onClick={() => handleOpenCancelItem(cart_item)}
                              >
                                Cancel Item dari Deal
                              </Button>
                            )}
                          </Col>
                        </Row>
                      </Container>
                    )
                  })}
                </div>
              </CardBody>
            </Card>
          </Collapse>

          <Collapse isOpen={isHistoryOpen}>
            <Card>
              <CardBody>
                <h2 className='mb-3 text-center'>History</h2>

                <ListGroup>
                  {(historyList || []).map((history) => {
                    const isActive = history?.id === historyDetail?.id
                    return (
                      <div key={history?.id}>
                        <ListGroupItem
                          action
                          active={isActive}
                          tag="button"
                          onClick={() => {
                            setShowDifferenceOnly(false)
                            if (isActive) {
                              clearHistoryDetail()
                            } else {
                              fetchHistoryDetail(history.id)
                            }
                          }}
                        >
                          {TimeUtil.format(history.created_at, 'YYYY-MM-DD HH:mm:ss')}: ({history.done_by}) {history.action_type.replaceAll('_', ' ')}
                        </ListGroupItem>

                        {isActive && (
                          <>
                            <Label className='text-gray-light text-nowrap mt-2'>
                              Lihat field yang berubah saja
                            </Label>
                            <Label className="custom-toggle mb-4">
                              <span></span>
                              <input
                                type="checkbox"
                                value={showDifferenceOnly}
                                onClick={() => setShowDifferenceOnly(!showDifferenceOnly)}
                              />
                              <span
                                className="custom-toggle-slider rounded-circle text-success"
                                data-label-off="No"
                                data-label-on="Yes"
                              />
                            </Label>

                            <Table striped>
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Before</th>
                                  <th>After</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tableData
                                  .filter(o => showDifferenceOnly ? !o.sameValue : true)
                                  .map((o, idx) => (
                                    <tr key={`${o.fieldName}_${idx}`}>
                                      <th scope="row">{o.fieldName}</th>
                                      <td>{o.before}</td>
                                      <td>{o.after}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </>
                        )}
                      </div>
                    )
                  })}
                </ListGroup>
              </CardBody>
            </Card>
          </Collapse>
        </div>
      </Card>

      <Modal isOpen={!!cancelItemTarget} toggle={handleCloseCancelItem} centered>
        <ModalHeader toggle={handleCloseCancelItem}>
          Konfirmasi Cancel Item
        </ModalHeader>

        <ModalBody>
          <p className="mb-3">
            {isLastDealItem
              ? 'Item ini adalah satu-satunya item dalam deal. Cancel item ini juga akan membatalkan seluruh Deal. Item dan nominal pembayaran tetap tersimpan sebagai riwayat untuk Finance.'
              : 'Item akan dikeluarkan dari deal. Sistem akan menghitung ulang Final Payment, menyesuaikan payment terakhir, dan mengembalikan SQ/IQ item ke inventory.'}
          </p>

          <Table bordered size="sm">
            <tbody>
              <tr>
                <th style={{ width: 160 }}>SKU</th>
                <td>{cancelItemTarget?.product?.sku || '-'}</td>
              </tr>
              <tr>
                <th>Product</th>
                <td>{salesDealProductDetailTitle(cancelItemTarget?.product)}</td>
              </tr>
              <tr>
                <th>Qty / Harga Satuan</th>
                <td>{cancelItemTarget?.quantity || 1} / {StringUtil.currencyCase(cancelItemTarget?.price)}</td>
              </tr>
              <tr>
                <th>Item Amount</th>
                <td>{StringUtil.currencyCase(Number(cancelItemTarget?.price || 0) * Number(cancelItemTarget?.quantity || 1))}</td>
              </tr>
              <tr>
                <th>Deal Status</th>
                <td>{StringUtil.capitalizeAll(StringUtil.humanize(deal?.state))}</td>
              </tr>
            </tbody>
          </Table>

          <Alert color="warning">
            {isLastDealItem
              ? 'PERINGATAN: Seluruh Deal akan menjadi Cancelled. Pastikan status fisik barang dan pembayaran sudah diperiksa sebelum melanjutkan.'
              : 'Pastikan status fisik barang sesuai sebelum cancel item. Owner/Admin dapat melakukan koreksi lintas status Deal; Sales hanya dapat cancel item pada Deal yang dibuat sendiri sebelum Waiting For Shipment.'}
          </Alert>

          <Label>Reason <span className="text-danger">*</span></Label>
          <Input
            type="textarea"
            rows="3"
            value={cancelItemReason}
            disabled={isCancellingItem}
            placeholder="Contoh: Customer cancel 1 item sebelum pickup"
            onChange={(event) => setCancelItemReason(event.target.value)}
          />

          {cancelItemResult && (
            <Alert color={cancelItemResult.color} className="mt-3 mb-0">
              {cancelItemResult.message}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            color="secondary"
            onClick={handleCloseCancelItem}
            disabled={isCancellingItem}
          >
            Back
          </Button>

          <Button
            color="danger"
            onClick={handleConfirmCancelItem}
            disabled={isCancellingItem || !cancelItemReason.trim()}
          >
            {isCancellingItem ? 'Cancelling Item...' : isLastDealItem ? 'Yes, Cancel Item & Deal' : 'Yes, Cancel Item'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isCancelConfirmOpen} toggle={() => !isCancellingDeal && setIsCancelConfirmOpen(false)} centered size="lg">
        <ModalHeader toggle={() => !isCancellingDeal && setIsCancelConfirmOpen(false)}>
          Konfirmasi Cancel Deal
        </ModalHeader>

        <ModalBody>
          <p className="mb-2">
            Yakin ingin membatalkan deal ini?
          </p>

          <Table bordered size="sm" className="mb-0">
            <tbody>
              <tr>
                <th style={{ width: 180 }}>Reference Number</th>
                <td>{deal?.reference_number || '-'}</td>
              </tr>
              <tr>
                <th>Customer</th>
                <td>{customerName}</td>
              </tr>
              <tr>
                <th>Final Payment</th>
                <td>{StringUtil.currencyCase(deal?.final_payment)}</td>
              </tr>
              <tr>
                <th>Status</th>
                <td>{StringUtil.capitalizeAll(StringUtil.humanize(deal?.state))}</td>
              </tr>
            </tbody>
          </Table>

          <h5 className="mt-3">Item yang akan dibatalkan ({(deal?.cart?.cart_items || []).length})</h5>
          <div style={{ maxHeight: '40vh', overflowY: 'auto' }}>
            <Table bordered responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>SKU / Nama / Spesifikasi</th>
                  <th>Qty</th>
                  <th>Harga Satuan</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {(deal?.cart?.cart_items || []).map((cartItem) => (
                  <tr key={cartItem?.id || cartItem?.product?.sku}>
                    <td style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                      <b>{cartItem?.product?.sku || '-'}</b><br />
                      {salesDealProductDetailTitle(cartItem?.product)}<br />
                      <span className="text-muted">
                        Spec: {cartItem?.product?.p_size || cartItem?.product?.spec || '-'}<br />
                        Condition: {getPoProductConditionName(cartItem?.product)}
                      </span>
                    </td>
                    <td>{cartItem?.quantity || 1}</td>
                    <td>{StringUtil.currencyCase(cartItem?.price)}</td>
                    <td>{StringUtil.currencyCase(Number(cartItem?.price || 0) * Number(cartItem?.quantity || 1))}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <Alert color="warning" className="mt-3 mb-0">
            Tindakan ini akan membatalkan <b>seluruh item di atas</b> dan mengubah status deal menjadi <b>Cancelled</b>.
          </Alert>
          {cancelResult && (
            <Alert color={cancelResult.color} className="mt-3 mb-0">
              {cancelResult.message}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setIsCancelConfirmOpen(false)}
            disabled={isCancellingDeal}
          >
            Back
          </Button>

          <Button
            color="danger"
            onClick={handleConfirmCancelDeal}
            disabled={isCancellingDeal}
          >
            {isCancellingDeal ? 'Cancelling...' : 'Yes, Cancel Deal'}
          </Button>
        </ModalFooter>
      </Modal>

    </>
  )
}

SalesDealsView.propTypes = propTypes
SalesDealsView.defaultProps = defaultValues

const SalesDealsViewWithAuth = (props) => {
  const { user } = useAuth()

  const isOwner =
    (user?.role || user?.access || '')
      .toString()
      .trim()
      .toLowerCase() === 'owner'

  return (
    <SalesDealsView
      {...props}
      isOwner={isOwner}
    />
  )
}

SalesDealsViewWithAuth.propTypes = propTypes
SalesDealsViewWithAuth.defaultProps = defaultValues

export default withLoader(SalesDealsViewWithAuth, CardLoader)
